import { AsyncStatus } from 'lib-shared-legacy-types';
import { combineActions, handleActions } from 'redux-actions';

import {
  NOTIFICATION_DEFAULT,
  OFFER_NOTIFICATION_INCOMING,
} from '../../../notifications/sagas/audio';
import { actions } from '../../actions/personal';
import { getLocalSettings } from '../../api/personal';

export const {
  personalSubmitMessageWithEnter,
  personalSubmitMessageWithEnterSucceeded,
  personalSubmitMessageWithEnterFailed,
  personalBusylight,
  personalBusylightSucceeded,
  personalBusylightFailed,
  personalKeyboardShortcuts,
  personalKeyboardShortcutsSucceeded,
  personalKeyboardShortcutsFailed,
  personalLeftMenu,
  personalLeftMenuSucceeded,
  personalLeftMenuFailed,
  personalCustomCardMenu,
  personalCustomCardMenuSucceeded,
  personalCustomCardMenuFailed,
  personalSound,
  personalSoundSucceeded,
  personalSoundFailed,
  personalLastFromEmailIntegration,
  personalLastFromEmailIntegrationSucceeded,
  personalLastFromEmailIntegrationFailed,
  personalConversationCards,
  personalReplyAreaHeightSucceeded,
  personalAddToFavouriteTeamMemberIdsSucceeded,
  personalRemoveFromFavouriteTeamMemberIdsSucceeded,
  personalToggleRememberMicrophoneAccessSucceeded,
  personalToggleRememberElevioCardOpen,
  personalChangedElevioSelectedLanguage,
  personalToggleRememberKnowledgeCardOpen,
} = actions;

type BusyLightTone =
  | 'OpenOffice'
  | 'Quiet'
  | 'Funky'
  | 'FairyTale'
  | 'KuandoTrain'
  | 'TelephoneNordic'
  | 'TelephoneOriginal'
  | 'TelephonePickMeUp'
  | 'Buzz';

type PersonalSoundType = {
  [key: string]: {
    tone: string;
  };
};

export type PersonalSettingsStatesT = {
  submitMessageWithEnter: boolean;
  keyboardShortcuts: boolean;
  notificationSound: string;
  meta: {
    asyncStatus: AsyncStatus;
  };
  busylight: Busylight | null;
  sounds: PersonalSoundType | null;
  isLeftMenuOpen: boolean;
  isCustomCardMenuOpen: boolean;
  skipMicrophoneAccess: boolean;
  lastUsedEmailIntegration: string | null;
  conversationCards: any;
  replyAreaHeight: number;
  favouriteTeamMemberIds: Array<string>;
  strictSearch: boolean;
  elevioCardOpen: boolean;
  elevioSelectedLanguage: string;
  knowledgeCardOpen: boolean;
};

export interface Busylight {
  /** The volume level. Note: Figure out what the range is and narrow the type. */
  soundVolume: number;
  /** The tone. */
  tone: BusyLightTone;
}

export type PersonalSettingsStates = PersonalSettingsStatesT;

function initialState(): PersonalSettingsStates {
  const localSettings = getLocalSettings();
  const initState = {
    submitMessageWithEnter: true,
    keyboardShortcuts: true,
    notificationSound: '',
    meta: {
      asyncStatus: 'succeeded' as AsyncStatus,
    },
    busylight: {
      soundVolume: 4,
      tone: 'TelephoneNordic' as BusyLightTone,
    },
    sounds: {
      pstnphone: { tone: OFFER_NOTIFICATION_INCOMING },
      widgetchat: { tone: OFFER_NOTIFICATION_INCOMING },
      email: { tone: OFFER_NOTIFICATION_INCOMING },
      messaging: { tone: OFFER_NOTIFICATION_INCOMING },
      notification: { tone: NOTIFICATION_DEFAULT },
    },
    isLeftMenuOpen: false,
    isCustomCardMenuOpen: false,
    skipMicrophoneAccess: false,
    lastUsedEmailIntegration: null,
    conversationCards: {},
    replyAreaHeight: 200,
    favouriteTeamMemberIds: [],
    strictSearch: false,
    elevioCardOpen: true,
    elevioSelectedLanguage: '',
    knowledgeCardOpen: true,
  };

  if (localSettings !== null) {
    if (localSettings.submitMessageWithEnter !== undefined) {
      initState.submitMessageWithEnter = localSettings.submitMessageWithEnter;
    }
    if (localSettings.notificationSound) {
      initState.notificationSound = localSettings.notificationSound;
    }
    if (localSettings.keyboardShortcuts !== undefined) {
      initState.keyboardShortcuts = localSettings.keyboardShortcuts;
    }
    if (localSettings.busylight) {
      initState.busylight = localSettings.busylight;
    }
    if (localSettings.isLeftMenuOpen) {
      initState.isLeftMenuOpen = localSettings.isLeftMenuOpen;
    }
    if (localSettings.sounds) {
      initState.sounds = localSettings.sounds;
    }
    if (localSettings.lastUsedEmailIntegration) {
      initState.lastUsedEmailIntegration =
        localSettings.lastUsedEmailIntegration;
    }
    if (localSettings.conversationCards) {
      initState.conversationCards = localSettings.conversationCards;
    }
    if (localSettings.replyAreaHeight) {
      initState.replyAreaHeight = localSettings.replyAreaHeight;
    }
    if (localSettings.favouriteTeamMemberIds) {
      initState.favouriteTeamMemberIds = localSettings.favouriteTeamMemberIds;
    }
    if (localSettings.strictSearch) {
      initState.strictSearch = localSettings.strictSearch;
    }
    if (localSettings.skipMicrophoneAccess) {
      initState.skipMicrophoneAccess = localSettings.skipMicrophoneAccess;
    }
    if (localSettings.isCustomCardMenuOpen) {
      initState.isCustomCardMenuOpen = localSettings.isCustomCardMenuOpen;
    }
    if (typeof localSettings.elevioCardOpen === 'boolean') {
      initState.elevioCardOpen = localSettings.elevioCardOpen;
    }
    if (localSettings.elevioSelectedLanguage) {
      initState.elevioSelectedLanguage = localSettings.elevioSelectedLanguage;
    }
    if (typeof localSettings.knowledgeCardOpen === 'boolean') {
      initState.knowledgeCardOpen = localSettings.knowledgeCardOpen;
    }
  }
  return initState;
}

// Reducer
export const personal = handleActions<PersonalSettingsStates, any>(
  {
    [personalSubmitMessageWithEnter.toString()]: (
      state: PersonalSettingsStates,
    ) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [personalSubmitMessageWithEnterSucceeded.toString()]: (
      state: PersonalSettingsStates,
      action: any,
    ) => {
      return {
        ...state,
        submitMessageWithEnter: action.payload.submitMessageWithEnter,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
      };
    },

    [personalSubmitMessageWithEnterFailed.toString()]: (
      state: PersonalSettingsStates,
    ) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [personalBusylight.toString()]: (state: PersonalSettingsStates) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [personalBusylightSucceeded.toString()]: (
      state: PersonalSettingsStates,
      action: any,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        busylight: {
          ...state.busylight,
          tone: action.payload.busylight.tone,
          soundVolume: action.payload.busylight.soundVolume,
        },
      };
    },

    [personalBusylightFailed.toString()]: (state: PersonalSettingsStates) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [personalKeyboardShortcuts.toString()]: (state: PersonalSettingsStates) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [personalKeyboardShortcutsSucceeded.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: { keyboardShortcuts: boolean } },
    ) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        keyboardShortcuts: action.payload.keyboardShortcuts,
      };
    },

    [personalKeyboardShortcutsFailed.toString()]: (
      state: PersonalSettingsStates,
    ) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [personalLeftMenu.toString()]: (state: PersonalSettingsStates) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [personalLeftMenuSucceeded.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: { isLeftMenuOpen: boolean } },
    ) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        isLeftMenuOpen: action.payload.isLeftMenuOpen,
      };
    },

    [personalLeftMenuFailed.toString()]: (state: PersonalSettingsStates) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [personalCustomCardMenu.toString()]: (state: PersonalSettingsStates) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [personalCustomCardMenuSucceeded.toString()]: (
      state: PersonalSettingsStates,
      action: {
        payload: { isCustomCardMenuOpen: boolean };
      },
    ) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        isCustomCardMenuOpen: action.payload.isCustomCardMenuOpen,
      };
    },

    [personalCustomCardMenuFailed.toString()]: (
      state: PersonalSettingsStates,
    ) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [personalSound.toString()]: (state: PersonalSettingsStates) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [personalSoundSucceeded.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: { sounds: any } },
    ) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        sounds: action.payload.sounds,
      };
    },

    [personalSoundFailed.toString()]: (state: PersonalSettingsStates) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [personalLastFromEmailIntegration.toString()]: (
      state: PersonalSettingsStates,
    ) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [personalLastFromEmailIntegrationSucceeded.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: { lastUsedEmailIntegration: string } },
    ) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        lastUsedEmailIntegration: action.payload.lastUsedEmailIntegration,
      };
    },

    [personalLastFromEmailIntegrationFailed.toString()]: (
      state: PersonalSettingsStates,
    ) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [personalLeftMenu.toString()]: (state: PersonalSettingsStates) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [personalLeftMenuSucceeded.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: { isLeftMenuOpen: boolean } },
    ) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        isLeftMenuOpen: action.payload.isLeftMenuOpen,
      };
    },

    [personalLeftMenuFailed.toString()]: (state: PersonalSettingsStates) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [personalConversationCards.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: any },
    ) => {
      return {
        ...state,
        conversationCards: {
          ...state.conversationCards,
          [action.payload.key.toString()]: action.payload.value,
        },
      };
    },

    [personalReplyAreaHeightSucceeded.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: any },
    ) => {
      return { ...state, replyAreaHeight: action.payload.replyAreaHeight };
    },

    [personalToggleRememberMicrophoneAccessSucceeded.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: { skipMicrophoneAccess: boolean } },
    ) => {
      return {
        ...state,
        skipMicrophoneAccess: action.payload.skipMicrophoneAccess,
      };
    },

    [combineActions(
      personalAddToFavouriteTeamMemberIdsSucceeded,
      personalRemoveFromFavouriteTeamMemberIdsSucceeded,
    ).toString()]: (
      state: PersonalSettingsStates,
      action: { payload: any },
    ) => {
      return {
        ...state,
        favouriteTeamMemberIds: action.payload.favouriteTeamMemberIds,
      };
    },

    [personalToggleRememberElevioCardOpen.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: { elevioCardOpen: boolean } },
    ) => {
      return {
        ...state,
        elevioCardOpen: action.payload.elevioCardOpen,
      };
    },

    [personalChangedElevioSelectedLanguage.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: { elevioSelectedLanguage: string } },
    ) => {
      return {
        ...state,
        elevioSelectedLanguage: action.payload.elevioSelectedLanguage,
      };
    },

    [personalToggleRememberKnowledgeCardOpen.toString()]: (
      state: PersonalSettingsStates,
      action: { payload: { knowledgeCardOpen: boolean } },
    ) => {
      return {
        ...state,
        knowledgeCardOpen: action.payload.knowledgeCardOpen,
      };
    },
  },
  initialState(),
);
